import { useId } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { formatNumber } from '@/lib/utils';
import { PaginationJump } from './pagination-jump';

const PAGE_SIZES = [25, 50, 100, 200] as const;

export interface TablePaginationProps {
  page: number;
  pageSize: number;
  total: number;
  /** No totals yet: an empty result and an unanswered query read the same. */
  loading?: boolean;
  /**
   * The query broke, so there is no total to report.
   *
   * Without this the footer read «Sin tareas que mostrar» — a fact about the
   * data — one line under a table saying it could not load any, which is the
   * difference between "there is nothing" and "we do not know".
   */
  failed?: boolean;
  /** Singular and plural of what is being counted: `['regla', 'reglas']`. */
  noun: readonly [string, string];
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}

/**
 * Where you are in the result set, and the two ways to move through it.
 *
 * Shared because every list screen ends in this bar, and two copies of it drift
 * within a release: «Filas» against «Por página», «1 / 24» against «Página 1 de
 * 24», a native picker against a floating one. None of those differences mean
 * anything, and all of them are visible when a person moves between two screens
 * of the same product. That drift already happened — six modules each ended up
 * with their own copy — and this is where they all came back.
 */
export function TablePagination({
  page,
  pageSize,
  total,
  loading = false,
  failed = false,
  noun,
  onPage,
  onPageSize,
}: TablePaginationProps) {
  const id = useId();
  const [singular, plural] = noun;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount);
  const first = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const last = Math.min(current * pageSize, total);

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-2.5">
      {/* «0–0 de 0 reglas» is arithmetic, not language: with nothing to page
          through, the range is noise and the sentence says so instead. */}
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {failed ? (
          `No pudimos contar el total de ${plural}`
        ) : loading ? (
          `Contando ${plural}…`
        ) : total === 0 ? (
          `Sin ${plural} que mostrar`
        ) : (
          <>
            <span className="tabular">
              {formatNumber(first)}–{formatNumber(last)}
            </span>{' '}
            de <span className="tabular">{formatNumber(total)}</span>{' '}
            {total === 1 ? singular : plural}
          </>
        )}
      </p>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Label htmlFor={id} className="text-xs font-normal text-muted-foreground">
            Filas
          </Label>
          <NativeSelect
            id={id}
            value={pageSize}
            onChange={(event) => onPageSize(Number(event.target.value))}
            containerClassName="w-auto"
            className="h-7 pl-1.5 text-xs tabular"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Página anterior"
            disabled={loading || current <= 1}
            onClick={() => onPage(current - 1)}
          >
            <ChevronLeft className="size-3.5" />
          </Button>
          <PaginationJump
            page={current}
            pageCount={pageCount}
            disabled={loading || pageCount <= 1}
            onPageChange={onPage}
          />
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Página siguiente"
            disabled={loading || current >= pageCount}
            onClick={() => onPage(current + 1)}
          >
            <ChevronRight className="size-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
